import { getImageSource, getResultBadges, getResultHost } from '../searchUtils';
import { ResultMetadata, SearchResult } from '../types';

export function ResultBadges({ badges }: { badges: string[] }) {
  if (!badges.length) {
    return null;
  }

  return (
    <div className="result-foot">
      {badges.map((badge, index) => (
        <span key={`${badge}-${index}`}>{badge}</span>
      ))}
    </div>
  );
}

function ResultPreview({ href, image, alt = '' }: { href?: string; image?: string; alt?: string }) {
  if (!image) {
    return <span className="image-fallback">No preview</span>;
  }

  if (!href) {
    return <img src={image} alt={alt} loading="lazy" />;
  }

  return (
    <a className="result-preview" href={href} target="_blank" rel="noreferrer" tabIndex={-1}>
      <img src={image} alt={alt} loading="lazy" />
    </a>
  );
}

export function ImageResultCard({ result, submittedQuery }: { result: SearchResult; submittedQuery: string }) {
  const imageSource = getImageSource(result);
  const href = result.url || result.img_src;

  return (
    <article className="result-card image-result">
      <a className="image-result-link" href={href} target="_blank" rel="noreferrer">
        <ResultPreview image={imageSource} alt={result.title || submittedQuery} />
        <span className="image-result-title">{result.title || result.url || 'Image result'}</span>
      </a>
      <ResultBadges badges={getResultBadges(result)} />
    </article>
  );
}

export function VideoResultCard({ result, submittedQuery }: { result: SearchResult; submittedQuery: string }) {
  const imageSource = getImageSource(result);

  return (
    <article className="result-card video-result">
      <a className="video-thumb" href={result.url} target="_blank" rel="noreferrer">
        {imageSource ? (
          <img src={imageSource} alt={result.title || submittedQuery} loading="lazy" />
        ) : (
          <span className="image-fallback">No preview</span>
        )}
        <span className="play-mark" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="m9 7 8 5-8 5V7Z" />
          </svg>
        </span>
      </a>
      <div className="result-copy">
        <a href={result.url} target="_blank" rel="noreferrer">
          {result.title || result.url}
        </a>
        {result.content && <p>{result.content}</p>}
        <ResultBadges badges={getResultBadges(result, { includeVideoMeta: true })} />
      </div>
    </article>
  );
}

export function TextResultCard({ result, metadata }: { result: SearchResult; metadata?: ResultMetadata }) {
  return (
    <article className="result-card result-item enriched-result">
      {metadata?.image && <ResultPreview href={result.url} image={metadata.image} />}
      <div className="result-copy">
        <a href={result.url} target="_blank" rel="noreferrer">
          {metadata?.title || result.title || result.url}
        </a>
        <p className="result-url">
          {metadata?.siteName || getResultHost(result.url) || result.url}
        </p>
        {(metadata?.description || result.content) && (
          <p>{metadata?.description || result.content}</p>
        )}
      </div>
      <ResultBadges badges={getResultBadges(result, { includeCategory: true })} />
    </article>
  );
}
